import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Card, Empty, Modal, Select, Space, Tag, Typography, Popconfirm, message,
  theme as antdTheme,
} from 'antd';
import { HolderOutlined } from '@ant-design/icons';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { space } from '@viq-admin/ui';
import { reorderById } from '../../utils/reorder';
import { QuestionForm } from '../../resources/questions/QuestionForm';
import {
  listConferenceQuestions, importInventory, deleteConferenceQuestion,
  reorderConferenceQuestions, createConferenceQuestion, updateConferenceQuestion,
  listQuestions, QuestionPayload,
} from '../../api/discovery';
import { ConferenceDiscoveryQuestion, DiscoveryQuestion } from '../../api/types';

function summary(q: ConferenceDiscoveryQuestion): string {
  if (q.type === 'free_text') return 'Free-text';
  const opts = q.options.map((o) => o.text);
  const head = opts.length <= 4 ? opts.join(' · ') : `${opts.length} options`;
  return `Choice · ${q.allowMultiple ? 'multi' : 'single'} · ${head}`;
}

function SortableCard({
  q, onEdit, onRemove,
}: {
  q: ConferenceDiscoveryQuestion;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: q.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const { token } = antdTheme.useToken();
  return (
    <Card ref={setNodeRef} style={{ ...style, marginBottom: space.sm }} styles={{ body: { padding: space.md } }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Space>
          <span {...attributes} {...listeners} style={{ cursor: 'grab', color: token.colorTextQuaternary }} aria-label="Drag to reorder">
            <HolderOutlined />
          </span>
          <div>
            <div>
              <b>{q.text}</b> {q.required && <Typography.Text type="danger">*</Typography.Text>}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>{summary(q)}</Typography.Text>
          </div>
        </Space>
        <Space>
          {q.required && <Tag color="gold">Required</Tag>}
          <Button size="small" onClick={onEdit}>Edit</Button>
          <Popconfirm
            title="Remove this question from the conference?"
            description={q.responseCount > 0 ? `${q.responseCount} captured answer${q.responseCount === 1 ? '' : 's'} will also be permanently deleted.` : undefined}
            onConfirm={onRemove}
          >
            <Button size="small" danger>Remove</Button>
          </Popconfirm>
        </Space>
      </Space>
    </Card>
  );
}

export function ConferenceDiscovery({
  conferenceId,
}: {
  conferenceId: string;
}) {
  const [questions, setQuestions] = useState<ConferenceDiscoveryQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [startedBlank, setStartedBlank] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [inventory, setInventory] = useState<DiscoveryQuestion[]>([]);
  const [picked, setPicked] = useState<string[]>([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ConferenceDiscoveryQuestion | null>(null);
  const [savedToInventory, setSavedToInventory] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor));

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setQuestions(await listConferenceQuestions(conferenceId));
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to load questions');
    } finally {
      setLoading(false);
    }
  }, [conferenceId]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function seedDefaults() {
    try {
      const imported = await importInventory(
        conferenceId,
        { defaults: true },
      );
      if (imported.length === 0) {
        // Nothing in the company's Inventory to copy — tell the user plainly
        // instead of silently reloading back to the empty state.
        message.info('No Inventory defaults to copy — set them up in your Inventory first.');
        return;
      }
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Seed failed');
    }
  }

  async function openAddModal() {
    setPicked([]);
    setAddOpen(true);
    try {
      setInventory(await listQuestions());
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Failed to load inventory');
    }
  }

  async function confirmAdd() {
    if (picked.length === 0) { setAddOpen(false); return; }
    try {
      await importInventory(conferenceId, { inventoryIds: picked });
      setAddOpen(false);
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Add failed');
    }
  }

  async function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const next = reorderById(questions, String(active.id), String(over.id), (q) => q.id);
    setQuestions(next);
    try {
      await reorderConferenceQuestions(conferenceId, next.map((q) => q.id));
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Reorder failed');
      await refresh();
    }
  }

  async function onRemove(id: string) {
    try {
      await deleteConferenceQuestion(conferenceId, id);
      await refresh();
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Remove failed');
    }
  }

  async function onSubmitEditor(payload: QuestionPayload) {
    try {
      if (editing) {
        await updateConferenceQuestion(conferenceId, editing.id, payload);
        setEditorOpen(false);
        setEditing(null);
        await refresh();
      } else {
        await createConferenceQuestion(conferenceId, payload);
        setEditorOpen(false);
        setSavedToInventory(true);
        await refresh();
      }
    } catch (err) {
      message.error((err as { message?: string }).message ?? 'Save failed');
    }
  }

  const showEmpty = !loading && questions.length === 0 && !startedBlank;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Alert
        type="info"
        message="These are copies — editing here won't change your Inventory or any other company's set. Drag to set the order reps see."
      />

      {showEmpty ? (
        <Empty description="No questions yet for this conference">
          <Space>
            <Button type="primary" onClick={seedDefaults}>Seed my Inventory defaults</Button>
            <Button onClick={() => setStartedBlank(true)}>Start blank</Button>
          </Space>
        </Empty>
      ) : (
        <>
          <Space>
            <Button onClick={openAddModal}>+ Add from Inventory</Button>
            <Button onClick={() => { setEditing(null); setEditorOpen(true); }}>+ New question</Button>
          </Space>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
              {questions.map((q) => (
                <SortableCard
                  key={q.id}
                  q={q}
                  onEdit={() => { setEditing(q); setEditorOpen(true); }}
                  onRemove={() => onRemove(q.id)}
                />
              ))}
            </SortableContext>
          </DndContext>

          {savedToInventory && (
            <Alert
              type="success"
              closable
              onClose={() => setSavedToInventory(false)}
              message='Creating a "New question" here also saved a copy to your Inventory. "Add from Inventory" pulls existing templates in without duplicating them.'
            />
          )}
        </>
      )}

      <Modal
        title="Add from Inventory"
        open={addOpen}
        onOk={confirmAdd}
        onCancel={() => setAddOpen(false)}
        okText="Add selected"
      >
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Pick questions to copy in"
          value={picked}
          onChange={setPicked}
          options={inventory.map((q) => ({ label: q.text, value: q.id }))}
          optionFilterProp="label"
        />
      </Modal>

      <Modal
        title={editing ? 'Edit question' : 'New question'}
        open={editorOpen}
        footer={null}
        onCancel={() => { setEditorOpen(false); setEditing(null); }}
        destroyOnClose
      >
        <QuestionForm
          // Conference editor: no "default" flag (default is an inventory concept).
          initial={editing as unknown as DiscoveryQuestion | null}
          showDefault={false}
          onSubmit={onSubmitEditor}
          onCancel={() => { setEditorOpen(false); setEditing(null); }}
        />
      </Modal>
    </Space>
  );
}
